import React from "react";
import { Link } from "react-router-dom";

import styles from "../Sidebar.module.css";

import {
  SidebarShortcutSmall,
  SidebarShortcutMedium,
  ShortcutsGroup,
} from "../SidebarShortcuts";

// Redux
import { useDispatch, useSelector } from "react-redux";

import {
  selectPatientId,
  selectPatientFullName,
  selectPatientUniqueID,
  resetPatient,
} from "../../../redux/features/patientState/patientStateSlice";
import {
  setActivePage,
  selectActivePatientSidebarGroup,
  setActivePatientSidebarGroup,
} from "../../../redux/features/applicationState/applicationStateSlice";

// Icons
import {
  UserGroupIcon,
  UserPlusIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import { ReactComponent as PhotoIcon } from "./Icons/photo.svg";
import { ReactComponent as ComparisonIcon } from "./Icons/comparison.svg";
import { ReactComponent as PsoriasisCalculationIcon } from "./Icons/psoriasis_calculation.svg";
import { ReactComponent as PsoriasisSegmentationIcon } from "./Icons/psoriasis_segmentation.svg";
import { ReactComponent as BodySegmentationIcon } from "./Icons/body_segmentation.svg";
import { ReactComponent as PasiStatisticsIcon } from "./Icons/pasi_statistics.svg";

const PatientsSlideOutPanel = () => {
  const dispatch = useDispatch();

  const ENTRY_MODULES = JSON.parse(process.env.REACT_APP_ENTRY_MODULES || "[]");

  const activePatientSidebarGroup = useSelector(
    selectActivePatientSidebarGroup
  );

  const patientId = useSelector(selectPatientId);
  const patientFullName = useSelector(selectPatientFullName);
  const patientUniqueID = useSelector(selectPatientUniqueID);

  const patientCardReset = () => {
    dispatch(resetPatient());
    dispatch(setActivePage("/patients/database"));
    dispatch(setActivePatientSidebarGroup("photo"));
  };

  return (
    <>
      {/* Selected patient menu */}
      {/* Selected patient header */}
      {patientId && (
        <div className="w-full">
          <div className="w-full pt-3 p-2">
            <div className="flex flex-row items-center justify-center bg-indigo-100 py-3 px-2 border border-neutral-300 rounded-xl">
              {/* Patient info */}
              <div className="flex flex-[3] flex-col items-center justify-center Xbg-purple-500">
                {/* Name */}
                <div className="flex flex-row items-start justify-start w-full">
                  <p className="text-lg md:text-xl font-semibold text-neutral-900">
                    {patientFullName ? patientFullName : "Nameless Patient"}
                  </p>
                </div>
                {/* ID */}
                <div className="flex flex-row items-start justify-start w-full">
                  <i className="text-base md:text-lg text-neutral-900">
                    {patientUniqueID}
                  </i>
                </div>
              </div>
              {/* Close patient */}
              <div className="flex flex-[1] flex-row items-center justify-end Xbg-orange-500">
                <Link
                  className="p-2 rounded-lg select-none
                           bg-indigo-50 hover:bg-red-50 active:bg-red-100
                            "
                  to={{ pathname: "/patients/database" }}
                  onClick={patientCardReset}
                >
                  <XMarkIcon className="h-8 w-8 text-neutral-900 stroke-2" />
                </Link>
              </div>
            </div>
          </div>

          {/* Gradient transition */}
          <div className="relative w-full z-[999]">
            <div className="absolute w-full h-5 bg-gradient-to-b from-0% from-white"></div>
          </div>
        </div>
      )}

      {/* Shortcuts groups */}
      <div
        className={`flex flex-col h-full w-full pt-3 p-2 overflow-y-scroll ${styles["hide-scrollbar"]} Xbg-blue-500`}
      >
        {patientId && (
          <>
            <div className="Xpb-2">
              <SidebarShortcutSmall
                shortcutText="Patient Details"
                Icon={DocumentTextIcon}
                pageLink={"/patients/details/" + patientId}
              />

              <SidebarShortcutSmall
                shortcutText="All Entries"
                Icon={ClipboardDocumentListIcon}
                pageLink={"/entries/all_entries_menu/" + patientId}
              />
            </div>

            {ENTRY_MODULES.includes("photo") && (
              <ShortcutsGroup
                groupText={"Photo"}
                groupHeight={192}
                groupName={"photo"}
                activeGroup={activePatientSidebarGroup}
                setActiveGroup={() => {
                  dispatch(setActivePatientSidebarGroup("photo"));
                }}
              >
                <SidebarShortcutSmall
                  shortcutText="Photo"
                  Icon={PhotoIcon}
                  pageLink={"/entries/photo_menu/" + patientId}
                />

                <SidebarShortcutSmall
                  shortcutText="Comparison"
                  Icon={ComparisonIcon}
                  pageLink={"/entries/comparison_menu/" + patientId}
                />
              </ShortcutsGroup>
            )}

            {ENTRY_MODULES.includes("psoriasis") && (
              <ShortcutsGroup
                groupText={"Psoriasis AI"}
                groupHeight={288}
                groupName={"psoriasisAiAssisted"}
                activeGroup={activePatientSidebarGroup}
                setActiveGroup={() => {
                  dispatch(setActivePatientSidebarGroup("psoriasisAiAssisted"));
                }}
              >
                <SidebarShortcutSmall
                  shortcutText="Psoriasis Calculation"
                  Icon={PsoriasisCalculationIcon}
                  pageLink={"/entries/psoriasis_menu/" + patientId}
                />

                <SidebarShortcutSmall
                  shortcutText="Psoriasis Segmentation"
                  Icon={PsoriasisSegmentationIcon}
                  pageLink={"/entries/psoriasis_segmentation_menu/" + patientId}
                />

                <SidebarShortcutSmall
                  shortcutText="PASI Statistics"
                  Icon={PasiStatisticsIcon}
                  pageLink={"/statistics/pasi_statistics/" + patientId}
                />
              </ShortcutsGroup>
            )}

            {ENTRY_MODULES.includes("ai_assisted") && (
              <ShortcutsGroup
                groupText={"AI-Assisted"}
                groupHeight={96}
                groupName={"aiAssisted"}
                activeGroup={activePatientSidebarGroup}
                setActiveGroup={() => {
                  dispatch(setActivePatientSidebarGroup("aiAssisted"));
                }}
              >
                <SidebarShortcutSmall
                  shortcutText="Body Segmentation"
                  Icon={BodySegmentationIcon}
                  pageLink={"/entries/segmentation_menu/" + patientId}
                />
              </ShortcutsGroup>
            )}

            <div className=" min-h-[1px] h-[1px] w-full my-2 bg-neutral-900 " />
          </>
        )}

        {/* Database and new patient shortcuts */}
        <SidebarShortcutMedium
          shortcutText={"Patient Database"}
          Icon={UserGroupIcon}
          pageLink="/patients/database"
        />
        <SidebarShortcutMedium
          shortcutText={"New Patient"}
          Icon={UserPlusIcon}
          pageLink="/patients/create"
        />
      </div>
    </>
  );
};

export default PatientsSlideOutPanel;
